import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { IAppNotification } from '../core.interface';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class Notification {
  private http = inject(HttpClient);

  private _notifications = signal<IAppNotification[]>([]);
  private _isLoading = signal<boolean>(false);
  private _hasMore = signal<boolean>(true);
  private _page = 0;
  private _size = 20;
  private _query: string | null = null;

  public notifications = computed(() => this._notifications());
  public isLoading = computed(() => this._isLoading());
  public hasMore = computed(() => this._hasMore());

  public unreadCount = computed(() => this._notifications().filter((n) => !n.isRead).length);

  public async fetchNotifications(q?: string, reset: boolean = false): Promise<void> {
    if (this._isLoading() || (!this._hasMore() && !reset)) return;

    if (reset) {
      this._notifications.set([]);
      this._page = 0;
      this._hasMore.set(true);
      this._query = q ?? null;
    }

    this._isLoading.set(true);

    try {
      const params: any = {
        page: this._page.toString(),
        size: this._size.toString(),
        onlyNotRead: 'false',
      };
      if (this._query) params.q = this._query;

      // Usando firstValueFrom para convertir Observable a Promise (como en Dart)
      const response: any = await firstValueFrom(
        this.http.get(`${environment.apiUrl}/user-notification`, { params }),
      );

      const content = response.data?.content || [];
      const fetched: IAppNotification[] = content.map((json: any) => ({
        ...json,
        notificationDate: new Date(json.notificationDate),
      }));

      this._notifications.update((prev) => [...prev, ...fetched]);
      this._hasMore.set(fetched.length === this._size);
      this._page++;
    } finally {
      this._isLoading.set(false);
    }
  }

  public async markAsRead(id: number): Promise<void> {
    await firstValueFrom(this.http.patch(`${environment.apiUrl}/user-notification/${id}/read`, {}));

    this._notifications.update((list) =>
      list.map((n) => (n.id === id ? { ...n, isRead: true } : n)),
    );
  }

  public clearNotifications(): void {
    this._notifications.set([]);
  }
}
